import React, { useState } from 'react';
import { css } from '@emotion/react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { OnwardLink } from '@/styles/blocks';
import * as ROUTES from '@/constants/routes';
import { INVOICE_TYPE_DISPLAY_MAPPINGS } from './constants';
import { RECORD_LABELS, RecordLink } from './DownstreamRecords';

const MIN_REASON_LENGTH = 10;

// Renders the endpoint's preview as-is; whether the group can be reverted is the endpoint's call.
const RevertDialog = ({ preview, submitting, onClose, onConfirm }) => {
    const [reason, setReason] = useState('');
    const invoices = preview?.invoices || [];
    const canConfirm =
        !!preview?.revertable && !!invoices.length && reason.trim().length >= MIN_REASON_LENGTH && !submitting;

    // A close mid-request would let the late response reopen the modal.
    const handleClose = () => {
        if (!submitting) onClose();
    };

    return (
        <Dialog open={!!preview} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Revert invoices</DialogTitle>
            <DialogContent>
                {preview?.loading && <CircularProgress size={24} />}
                {preview?.error && <MuiAlert severity="error">{preview.error}</MuiAlert>}
                {!!invoices.length && (
                    <>
                        <DialogContentText>
                            These invoices share orders, so they are reverted together. Each NetSuite record is deleted
                            and each Bill.com bill archived first. Only if every one of those succeeds are the invoices
                            marked <strong>REVERTED</strong> and their orders released back to Completed Orders;
                            otherwise nothing is released and the failures are shown on the invoice.
                        </DialogContentText>
                        {invoices.map((inv) => (
                            <div
                                key={inv.carrier_invoice_id}
                                css={css`
                                    margin-bottom: 12px;
                                `}
                            >
                                <div>
                                    <OnwardLink
                                        href={ROUTES.ACCOUNTING_INVOICE.replace(':invoice_id', inv.carrier_invoice_id)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        #{inv.invoice_number}
                                    </OnwardLink>{' '}
                                    · {INVOICE_TYPE_DISPLAY_MAPPINGS[inv.type] || inv.type} · {inv.client_name || '--'}{' '}
                                    · {inv.status}
                                </div>
                                {inv.legs.map((leg) => (
                                    <div key={`${leg.transaction_type}-${leg.transaction_id}`}>
                                        {RECORD_LABELS[leg.transaction_type]}{' '}
                                        <RecordLink record={leg} invoiceType={inv.type} /> will be{' '}
                                        {leg.action === 'DELETE' ? 'deleted' : 'archived'}
                                    </div>
                                ))}
                                {inv.blockers.map((blocker) => (
                                    <MuiAlert key={blocker} severity="warning">
                                        {blocker}
                                    </MuiAlert>
                                ))}
                            </div>
                        ))}
                        <TextField
                            id="revert-reason"
                            autoFocus
                            margin="dense"
                            label={`Reason (required, min ${MIN_REASON_LENGTH} chars)`}
                            fullWidth
                            multiline
                            minRows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    color="secondary"
                    variant="contained"
                    disabled={!canConfirm}
                    onClick={() => onConfirm(reason.trim())}
                >
                    {`Revert ${invoices.length} invoice${invoices.length === 1 ? '' : 's'}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RevertDialog;
